import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { bonoBotState, setDonationAmount } from 'store/bonoBot/bonoBot.slice';
import { getCalculatedAmount, getImactCarouselData } from 'utils/utils';
import { AmountExampleType } from 'store/bonoBot/types';
import { routes } from 'routes/routes';
import { useSocketInstanceContext } from 'context/socketInstanceContext';

const AmountExample: React.FC<AmountExampleType> = ({ amount }) => {
   const { t } = useTranslation();
   const navigate = useNavigate();
   const dispatch = useAppDispatch();
   const { sendMessageToServer, sendMessageToServerAndChat, closeSocket } = useSocketInstanceContext();
   const { selectedCharities, storedThreadId } = useAppSelector(bonoBotState);
   const [selectedAmount, setSelectedAmount] = useState<number>(amount ?? 20);
   const charityToShow = getImactCarouselData(selectedCharities, 1)?.[0];
   if (selectedCharities.length === 0) return null;
   const { indicatorPrice, indicatorTitle, indicatorImage } = charityToShow;
   const calculatedEffect = getCalculatedAmount(selectedAmount, indicatorPrice);

   const getAmountsArray = (am: number | undefined) => {
      if (!am) return [10, 20, 30];
      if (am < 10) return [5, 10, 20];
      return [am - 10, am, am + 10];
   };
   const amountsArray = getAmountsArray(amount);

   const getImactImagePosition = (optionAmount: number) => {
      if (optionAmount === amountsArray[0]) return 'start';
      if (optionAmount === amountsArray[1]) return 'center';
      if (optionAmount === amountsArray[2]) return 'end';
      return 'center';
   };

   const handleChooseAmount = async () => {
      dispatch(setDonationAmount(selectedAmount));
      const text = `I choose donation amount $${selectedAmount}`;
      sendMessageToServer(text, storedThreadId);
      closeSocket();
      navigate(routes().bonoBot.summary);
   };

   return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
         <Box sx={{ display: 'flex', flexDirection: 'row', columnGap: 1.5, justifyContent: 'center' }}>
            {amountsArray.map((amountOption: number) => (
               <Box
                  key={amountOption}
                  onClick={() => setSelectedAmount(amountOption)}
                  sx={{
                     position: 'relative',
                     width: '94px',
                     height: '85px',
                     backgroundColor: '#FFFFFF',
                     border: selectedAmount === amountOption ? '2px solid #F55D66' : '1px solid #00000010',
                     borderRadius: '8px',
                     display: 'flex',
                     justifyContent: 'center',
                     alignItems: 'center',
                     cursor: 'pointer',
                  }}
               >
                  <Typography sx={{ fontSize: '24px', lineHeight: '29px', fontWeight: 700, color: '#333333' }}>
                     ${amountOption}
                  </Typography>
                  {selectedAmount === amountOption && (
                     <Box
                        sx={{
                           zIndex: 51,
                           borderLeft: '1px solid #00000010',
                           borderTop: '1px solid #00000010',
                           top: 77,
                           backgroundColor: '#FFFFFF',
                           position: 'absolute',
                           width: '30px',
                           height: '30px',
                           rotate: '45deg',
                        }}
                     />
                  )}
               </Box>
            ))}
         </Box>
         {/* impact amount */}
         <Box
            sx={{
               mt: 1.25,
               width: '100%',
               display: 'flex',
               flexDirection: 'row',
               justifyContent: getImactImagePosition(selectedAmount),
            }}
         >
            <Box
               sx={{
                  position: 'relative',
                  width: '192px',
                  height: '100px',
                  padding: '4px',
                  backgroundColor: '#FFFFFF',
                  borderRadius: '6px',
                  border: '1px solid #00000010',
               }}
            >
               <Box
                  sx={{
                     position: 'absolute',
                     top: 4,
                     left: 4,
                     zIndex: 55,
                     borderRadius: '4px',
                     width: '182px',
                     height: '90px',
                     overflow: 'hidden',
                     backgroundSize: 'cover',
                     backgroundPosition: 'center',
                     backgroundImage: `url(${indicatorImage})`,
                     display: 'flex',
                     flexDirection: 'column',
                     justifyContent: 'end',
                  }}
               >
                  <Box
                     sx={{
                        px: '10px',
                        paddingBottom: '10px',
                        height: '50%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'end',
                        background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)',
                     }}
                  >
                     <Typography
                        sx={{
                           fontFamily: 'Figtree',
                           color: '#ffffff',
                           fontSize: '24px',
                           fontWeight: 700,
                           lineHeight: '29px',
                        }}
                     >
                        {calculatedEffect}
                     </Typography>
                     <Typography
                        sx={{
                           fontFamily: 'Figtree',
                           fontWeight: 600,
                           color: '#ffffff',
                           fontSize: '14px',
                           lineHeight: '17px',
                        }}
                     >
                        {indicatorTitle}
                     </Typography>
                  </Box>
               </Box>
            </Box>
         </Box>

         <Box sx={{ display: 'flex', flexDirection: 'row', gap: '4px', mt: 1.25 }}>
            <Box
               onClick={handleChooseAmount}
               sx={{
                  cursor: 'pointer',
                  border: '1px solid #00000010',
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                  px: 1.5,
                  py: 1.375,
               }}
            >
               <Typography sx={{ color: '#666666', fontSize: '14px', lineHeight: '17px', fontWeight: 600 }}>
                  {t('bonoBot.amountExample.cta-chooseThisAmount')}
               </Typography>
            </Box>
            <Box
               onClick={() => {
                  const text = `Suggest me amount to donate`;
                  sendMessageToServerAndChat(text, storedThreadId);
               }}
               sx={{
                  cursor: 'pointer',
                  border: '1px solid #00000010',
                  borderRadius: '8px',
                  backgroundColor: '#FFFFFF',
                  px: 1.5,
                  py: 1.375,
               }}
            >
               <Typography sx={{ color: '#666666', fontSize: '14px', lineHeight: '17px', fontWeight: 600 }}>
                  {t('bonoBot.amountExample.cta-otherAmount')}
               </Typography>
            </Box>
            {/* {canChooseMoreCharities && (
               <Box
                  onClick={() => {
                     const text = `I want to choose more charities`;
                     sendMessageToServer(text, storedThreadId);
                  }}
                  sx={{
                     cursor: 'pointer',
                     border: '1px solid #00000010',
                     borderRadius: '8px',
                     backgroundColor: '#FFFFFF',
                     px: 1.5,
                     py: 1.375,
                  }}
               >
                  <Typography sx={{ color: '#666666', fontSize: '14px', lineHeight: '17px', fontWeight: 600 }}>
                     I want to choose more charities
                  </Typography>
               </Box>
            )} */}
         </Box>
      </Box>
   );
};

export default AmountExample;
